import React from 'react';
import { DropzoneRenderArgs } from 'react-dropzone';
import { bytesToSize } from 'utils/number';
import { QuestionBody, QuestionWrapper } from './styles';
import messages from './Messages';
import TitleInput from './TitleInput';
import FileUpload from 'components/FileUpload';
import DescriptionInput, {
  getDescriptionInputStatus,
} from './DescriptionInput';
import notify from 'components/Notify';
import {
  IFileAnswerView,
  IFileQuestionView,
  IAnswerView,
  IQuestionsView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { ITags } from 'store/providers/providerTypes';
import { IDocumentView } from 'store/companies/companiesTypes';
import i18n from '../../i18n';
import Button from '../Button/Button';
import SurveyAudioRecorder from './SurveyAudioRecorder/SurveyAudioRecorder';
import Compressor from 'compressorjs';
import {
  releaseUploadFile,
  rememberUploadFile,
} from 'utils/pendingUploadFiles';

interface IFileQuestionProps {
  answer?: IFileAnswerView;
  isRequired: boolean;
  disabled?: boolean;
  onChange: (newAnswer: any, questionId: number) => void;
  question: IFileQuestionView;
  tags?: ITags['data'];
  survey?: ISurveyView;
  draft?: boolean;
  answers?: { [key: number]: IAnswerView };
  questions?: { [questionId: number]: IQuestionsView };
  getNewsComplementaryContext?: () => string;
  aiGptModel?: string;
}

interface IFileQuestionState {
  file: IDocumentView | undefined;
  rejected: DropzoneRenderArgs['rejectedFiles'];
  addDescription: boolean;
  enabaleRecordingVoice: boolean;
}

class FileQuestion extends React.Component<
  IFileQuestionProps,
  IFileQuestionState
> {
  constructor(props: IFileQuestionProps) {
    super(props);
    const { answer } = props;
    this.state = {
      file: answer && answer.file,
      rejected: [],
      addDescription: !answer || (answer && !answer.description),
      enabaleRecordingVoice:
        !!props.question.validFormat &&
        props.question.validFormat?.includes('audio') &&
        props.question.validFormat.length === 1,
    };
  }

  shouldComponentUpdate(
    nextProps: Readonly<IFileQuestionProps>,
    nextState: Readonly<IFileQuestionState>
  ): boolean {
    return (
      nextProps.answer !== this.props.answer ||
      nextProps.question !== this.props.question ||
      nextProps.isRequired !== this.props.isRequired ||
      nextState !== this.state
    );
  }
  componentWillUnmount() {
    // revoke the data uris to avoid memory leaks
    const { file } = this.state;
    if (file && file.url) {
      URL.revokeObjectURL(file.url);
    }
  }

  handleAcceptedFiles = (files: DropzoneRenderArgs['acceptedFiles']) => {
    const { answer, question, onChange } = this.props;
    const acceptedFile = files[0];
    const { name, size, type } = acceptedFile;
    if (this.state.file?.url) releaseUploadFile(this.state.file.url);
    const objectUrl = URL.createObjectURL(acceptedFile);
    rememberUploadFile(objectUrl, acceptedFile);
    const file: IDocumentView = {
      url: objectUrl,
      fileSize: size,
      uploading: true,
      properties: {
        format: type,
        displayName: name,
      },
    };
    this.setState({
      file,
    });
    const newAnswer = {
      ...answer,
      file: { uploading: true, ...file },
    };
    onChange(newAnswer, question.id);
  };

  handleRejectedFiles = (files: DropzoneRenderArgs['rejectedFiles']) => {
    const file = files[0];
    const maxSize = (this.props.question.maxSizeInKB || 512) * 1024;
    let warningMessage;
    if (file.size > maxSize) {
      warningMessage = messages.invalidSize(bytesToSize(maxSize));
    } else {
      const type = file.type.split('/')[1];
      warningMessage = messages.invalidType(type);
    }
    notify(warningMessage, 'warning');
  };

  handleRemoveFiles = () => {
    const { answer, question, onChange } = this.props;

    if (this.state.file?.url) releaseUploadFile(this.state.file.url);
    // const receivedFile = this.state.accepted.slice(0).splice(1, 1);
    this.setState({
      file: undefined,
    });
    const newAnswer = { ...answer, file: undefined };
    onChange(newAnswer, question.id);
  };

  handleChangeDescription = (value: any, name: any) => {
    const { answer, question, onChange } = this.props;

    const newAnswer = { ...answer, [name]: value };
    onChange(newAnswer, question.id);
  };

  handleAddVoice = (audioUrl: string, size: number, format: string) => {
    const { answer, question, onChange } = this.props;

    if (this.state.file?.url && this.state.file.url !== audioUrl) {
      releaseUploadFile(this.state.file.url);
    }

    const file: IDocumentView = {
      url: audioUrl,
      fileSize: size,
      uploading: true,
      properties: {
        format: format,
        displayName: 'record',
      },
    };

    this.setState({
      file,
    });

    const newAnswer = {
      ...answer,
      file: { uploading: true, ...file },
    };

    onChange(newAnswer, question.id);
  };

  handleImageGenerated = (dataUri: string) => {
    const mime = dataUri.match(/data:([^;]+);/)?.[1] ?? 'image/png';
    const extension = mime.split('/')[1] || 'png';
    const base64 = dataUri.split(',')[1];

    if (!base64) {
      return;
    }

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const generatedFile = new File([bytes], `ai-generated.${extension}`, {
      type: mime,
    });
    const { size } = generatedFile;
    const maxFileSize = (this.props.question.maxSizeInKB || 512) * 1024;
    if (size > maxFileSize) {
      new Compressor(generatedFile, {
        quality: Math.sqrt(maxFileSize / size),
        success: (result: any) => {
          if (result.size < maxFileSize) this.handleAcceptedFiles([result]);
          else
            new Compressor(generatedFile, {
              quality: (maxFileSize / size) * 0.8,
              convertSize: maxFileSize,
              success: (result: any) => {
                if (result.size < maxFileSize)
                  this.handleAcceptedFiles([result]);
                else
                  notify(
                    i18n.t('failedToCompressImage', { ns: 'survey' }) + ' 1',
                    'warning'
                  );
              },
              error: () => {
                notify(
                  i18n.t('failedToCompressImage', { ns: 'survey' }) + ' 2',
                  'warning'
                );
              },
            });
        },
        convertSize: maxFileSize,
        error: () => {
          notify(
            i18n.t('failedToCompressImage', { ns: 'survey' }) + ' 3',
            'warning'
          );
        },
      });
    } else this.handleAcceptedFiles([generatedFile]);
  };

  descriptionHandler = () => {
    this.setState({ addDescription: !this.state.addDescription });
  };

  render() {
    const { question, answer, isRequired, disabled, tags, survey, draft } =
      this.props;
    const { file } = this.state;

    // define customIsRequired because of needing it is being false event though it is required while adding some desc to it.
    const customIsRequired =
      (answer?.description || '').length > 0 ? false : isRequired;

    const descriptionStatus = survey
      ? getDescriptionInputStatus(survey, question)
      : true;

    return (
      <QuestionWrapper disabled={disabled}>
        <TitleInput
          fields={question.fields || []}
          hint={question.hint}
          required={question.answerRequired}
          title={question.title}
          tags={question.tags}
          allTags={tags}
          indexNumber={question.indexOrder}
          survey={survey}
          answers={this.props.answers}
          questions={this.props.questions}
          getNewsComplementaryContext={this.props.getNewsComplementaryContext}
          aiGptModel={this.props.aiGptModel}
          onImageGenerated={this.handleImageGenerated}
        />
        {!question.validFormat ||
          (question.validFormat?.includes('audio') && (
            <p className="flex w-full justify-end px-5">
              <Button
                onClick={() =>
                  this.setState((prevState) => ({
                    ...prevState,
                    enabaleRecordingVoice: !prevState.enabaleRecordingVoice,
                  }))
                }
                color="primary-outline"
              >
                {this.state.enabaleRecordingVoice
                  ? (i18n.t('addFile', { ns: 'survey' }) as string)
                  : (i18n.t('recordVoice', { ns: 'survey' }) as string)}
              </Button>
            </p>
          ))}
        <QuestionBody>
          {this.state.enabaleRecordingVoice ? (
            <SurveyAudioRecorder
              addVoice={this.handleAddVoice}
              removeVoice={this.handleRemoveFiles}
              required={customIsRequired}
              maxSize={(question.maxSizeInKB || 512) * 1024}
              file={(answer && answer.file) || file}
            />
          ) : (
            <FileUpload
              file={(answer && answer.file) || file}
              name={question.id.toString()}
              maxSize={(question.maxSizeInKB || 512) * 1024}
              required={draft ? false : customIsRequired}
              onRemove={this.handleRemoveFiles}
              onAccepted={this.handleAcceptedFiles}
              onRejected={this.handleRejectedFiles}
              validFormats={question.validFormat}
              allowCompressImage={true}
              autoOpenEnabled={
                !!question.validFormat &&
                question.validFormat?.includes('audio') &&
                question.validFormat.length === 1
              }
            />
          )}

          {descriptionStatus && (
            <DescriptionInput
              question={question}
              survey={survey!}
              open={!this.state.addDescription}
              handler={this.descriptionHandler}
              id={question.id}
              text={answer && answer.description}
              onChangeInput={this.handleChangeDescription}
              disabled={disabled}
            />
          )}
        </QuestionBody>
      </QuestionWrapper>
    );
  }
}

export default FileQuestion;
